import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RichTextEditorComponent } from 'app/shared/components/forms/fields/rich-text-editor/rich-text-editor.component';
import { CaseFormSectionContainerComponent } from "../../shared/components/forms/case-form-section-container/case-form-section-container.component";
import { NewsPreviewComponent } from "../../shared/components/news-preview/news-preview.component";
import { ProfileEditComponent } from "../../shared/components/profile-edit/profile-edit.component";
import { WriteArticleFormComponent } from "../../shared/components/forms/write-article-form/write-article-form.component";
import { NewsInformationFormComponent } from "../../shared/components/forms/news-information-form/news-information-form.component";
import { RelatedNewsComponent } from "../../shared/components/related-news/related-news.component";
import { PaginationComponent } from "../../shared/components/pagination/pagination.component";
import { NewsViewHeaderComponent } from "../../shared/components/news-view-header/news-view-header.component";
import { CreateNewsRequest, NewsArticle, RelatedNewsArticle, UpdateNewsRequest } from './news-profile.model';
import { NewsService } from './news-profile.service';
import { ConfirmationDialogComponent } from 'app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, take, takeUntil } from 'rxjs';
import { Mode } from 'app/shared/enums/news-admin.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ToastTypesEnum } from 'app/shared/enums/toast.enum';
import { BackNavigationComponent } from "../../shared/components/back-navigation/back-navigation.component";

@Component({
  selector: 'app-news-profile',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NgSelectModule,
    FormsModule,
    ReactiveFormsModule,
    RichTextEditorComponent,
    CaseFormSectionContainerComponent,
    NewsPreviewComponent,
    ProfileEditComponent,
    WriteArticleFormComponent,
    NewsInformationFormComponent,
    RelatedNewsComponent,
    PaginationComponent,
    NewsViewHeaderComponent,
    BackNavigationComponent
],
  templateUrl: './news-profile.component.html',
  styleUrls: ['./news-profile.component.scss'],
})
export class NewsProfileComponent implements OnInit, OnDestroy {
  @Input() hideTabs: boolean = false;
  @Output() toggleTabs = new EventEmitter<boolean>();

  @ViewChild(NewsInformationFormComponent) newsInfoForm!: NewsInformationFormComponent;
  relatedNewsIdsToAdd: number[] = [];
  relatedNewsIdsToDelete: number[] = [];
  relatedNews: RelatedNewsArticle[] = [];
  news: NewsArticle[] = [];
  displayedNews: NewsArticle[] = [];
  currentPage: number = 1;
  totalNewsCount: number = 0;
  selectedPageSize: number = 5;
  pageSizeOptions = [5, 10, 20];
  selectedNews: NewsArticle | null = null;
  currentMode: Mode | null = null;
  Mode = Mode;
  isCreatedMode: boolean = false;
  availableNewsItems: number[] = [];
  addRelatedNews!: number;
  relatedNewsEdit!: boolean;

  showForm: boolean = false;
  form!: FormGroup;
  authors: { id: number, name: string }[] = [];
  isPublishDisabled: boolean = true;
  isPreviewDisabled: boolean = false;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private newsService: NewsService,
    private modalService: NgbModal,
    private fb: FormBuilder,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.trackFormChanges();

    this.activeRoute.params.subscribe(params => {
      const id = params['id'];
      const path = this.activeRoute.snapshot.routeConfig?.path;

      if (id) {
        this.setMode(path);
        this.loadNewsById(+id);
      } else {
        this.currentMode = null;
        this.selectedNews = null;
        this.getNews();
      }
    });
  }

  setMode(path: string | undefined): void {
    if (!path) {
      this.currentMode = Mode.View;
      return;
    }
    this.currentMode = path.includes('/edit/') ? Mode.Edit
      : path.includes('/delete/') ? Mode.Delete
        : Mode.View;
  }

  loadNewsById(id: number): void {
    this.newsService.getNews(id).subscribe(detailedNewsItem => {
      this.selectedNews = detailedNewsItem;
      this.relatedNews = detailedNewsItem.relatedNewsArticles || [];
      this.authors = [{
        id: detailedNewsItem.autorId,
        name: detailedNewsItem.autorName
      }];
      this.form.patchValue({
        articleTitle: detailedNewsItem.title,
        articleContent: detailedNewsItem.body || detailedNewsItem.shortBody || '',
        author: detailedNewsItem.autorId,
      });
      this.showForm = this.currentMode !== Mode.View;
      this.toggleTabs.emit(this.showForm);
      const relatedNewsIds = this.relatedNews.map(news => news.id);
      this.form.get('relatedNewsDropdown')?.setValue(relatedNewsIds);

      this.fetchAllNewsForDropdown(detailedNewsItem.id);
    });
  }

  fetchAllNewsForDropdown(currentId: number | null): void {
    const params = {
      skipCount: 0,
      takeCount: 1000,
      orderField: 'Id',
      isOrderByDescending: true,
      ...(currentId ? { CurrentId: currentId } : {}),
    };

    this.newsService.getAllNews(params)
    .pipe(take(1))
    .subscribe(allNews => {
      this.news = allNews.filter(news => news.id !== currentId);
      this.availableNewsItems = this.news.map(news => news.id);
      this.updateAvailableNews();
    });
  }

  updateAvailableNews(): void {
    const relatedNewsIds = this.relatedNews.map(news => news.id);
    this.availableNewsItems = this.availableNewsItems.filter(
      id => !relatedNewsIds.includes(id) && !this.relatedNewsIdsToDelete.includes(id)
    );

    this.form.get('relatedNewsDropdown')?.setValue(relatedNewsIds);
  }

  initializeForm(): void {
    this.form = this.fb.group({
      articleTitle: ['', Validators.required],
      articleContent: ['', Validators.required],
      author: [null, Validators.required],
      relatedNewsDropdown: new FormControl(null)
    });

    this.isPublishDisabled = this.currentMode === Mode.Create;
    this.isPreviewDisabled = this.currentMode === Mode.Create;
  }

  trackFormChanges(): void {
    this.form.get('articleTitle')?.valueChanges.subscribe((value: string) => {
      if (value?.startsWith(' ')) {
        const trimmedValue = value.trimStart();
        this.form.get('articleTitle')?.setValue(trimmedValue, { emitEvent: false });
      }
    });

    this.form.valueChanges.subscribe(() => {
      const isRelatedNewsModified =
        this.relatedNewsIdsToAdd.length > 0 ||
        this.relatedNewsIdsToDelete.length > 0;

      this.isPublishDisabled = this.currentMode === Mode.Create
        ? !(this.form.get('articleTitle')?.valid && this.form.get('articleContent')?.valid)
        : !(this.form.dirty || isRelatedNewsModified);

      if (this.currentMode === Mode.Create) {
        const titleValue = this.form.get('articleTitle')?.value?.trim() || '';
        const contentValue = this.form.get('articleContent')?.value?.trim() || '';
        this.isPreviewDisabled = !(titleValue || contentValue);
      } else {
        this.isPreviewDisabled = false;
      }
    });
  }

  onCreateNews(): void {
    this.currentMode = Mode.Create;
    this.selectedNews = null;
    this.showForm = true;
    this.toggleTabs.emit(true);
    this.form.reset();
    this.relatedNews = [];
    this.relatedNewsIdsToAdd = [];
    this.relatedNewsIdsToDelete = [];
    this.isCreatedMode = true;
    this.fetchAllNewsForDropdown(null);
  }


  submitNewArticle(): void {
    const newArticle: CreateNewsRequest = {
      title: this.form.value.articleTitle,
      body: this.form.value.articleContent,
      autorId: this.form.value.author,
      relatedNewsArticleIds: this.relatedNewsIdsToAdd,
    };

    this.newsService.createNews(newArticle)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((createdArticle) => {
        if (createdArticle.publishDate) {
          createdArticle.dateTime = new Date(createdArticle.publishDate);
        }
        this.news.unshift(createdArticle);
        this.resetRelatedNewsChanges();
        this.onGoBack();

        const successMessage = this.translate.instant('messages.news_successfully_created');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);
        this.isCreatedMode = false;
        this.getNews();
      });
  }

  onEditNews(newsItem: NewsArticle): void {
    this.router.navigate(['/admin/news/edit', newsItem.id]).then(() => {
      this.currentMode = Mode.Edit;
      this.selectedNews = newsItem;
      this.showForm = true;
      this.toggleTabs.emit(true);
    });
  }

  submitUpdatedArticle(): void {
    if (!this.selectedNews) return;

    const relatedNewsArticleIds = [
      ...this.relatedNewsIdsToAdd,
      ...(this.selectedNews.relatedNewsArticles || [])
        .map(article => article.id)
        .filter(id => !this.relatedNewsIdsToDelete.includes(id))
    ];

    const updatedArticle: UpdateNewsRequest = {
      title: this.form.value.articleTitle || '',
      body: this.form.value.articleContent || '',
      autorId: this.form.value.author,
      relatedNewsArticleIds
    };

    this.newsService.updateNews(this.selectedNews.id, updatedArticle)
      .pipe(take(1))
      .subscribe(() => {
        const successMessage = this.translate.instant('messages.news_successfully_updated');
        this.toastr.show(successMessage, ToastTypesEnum.SUCCESS);

        this.resetRelatedNewsChanges();
        this.form.markAsPristine();
        this.isPublishDisabled = true;
        this.fetchAllNewsForDropdown(this.selectedNews?.id || null);
      });
  }

  resetRelatedNewsChanges(): void {
    this.relatedNewsIdsToAdd = [];
    this.relatedNewsIdsToDelete = [];
  }

  onAddRelatedNews(newsIds: number[]): void {
    newsIds.forEach(newsId => {
      if (!this.relatedNewsIdsToAdd.includes(newsId) && !this.relatedNewsIdsToDelete.includes(newsId)) {
        this.relatedNewsIdsToAdd.push(newsId);
      }
      this.relatedNewsIdsToDelete = this.relatedNewsIdsToDelete.filter(id => id !== newsId);
      this.availableNewsItems = this.availableNewsItems.filter(id => id !== newsId);
    });

    this.updateAvailableNews();
  }

  onDeleteRelatedNews(newsId: number): void {
    if (!this.relatedNewsIdsToDelete.includes(newsId) && !this.relatedNewsIdsToAdd.includes(newsId)) {
      this.relatedNewsIdsToDelete.push(newsId);
    }
    this.relatedNewsIdsToAdd = this.relatedNewsIdsToAdd.filter(id => id !== newsId);

    if (!this.availableNewsItems.includes(newsId)) {
      this.availableNewsItems.push(newsId);
    }
    this.updateAvailableNews();
  }

  onPreviewArticle(): void {
    this.currentMode = Mode.Preview;
    this.showForm = false;

    this.selectedNews = {
      id: this.selectedNews?.id || 0,
      title: this.form.value.articleTitle || 'Untitled',
      body: this.form.value.articleContent || '',
      shortBody: this.form.value.articleContent?.substring(0, 100) || '',
      autorId: this.form.value.author,
      autorName: this.authors.find(author => author.id === this.form.value.author)?.name || '',
      publishDate: this.selectedNews?.publishDate || new Date(),
      viewsCount: this.selectedNews?.viewsCount || 0,
      relatedNewsArticles: this.selectedNews?.relatedNewsArticles || [],

      text: this.form.value.articleContent || '',
      author: this.authors.find(author => author.id === this.form.value.author)?.name || '',
      dateTime: this.selectedNews?.publishDate || new Date(),
      views: this.selectedNews?.views || 0,
      image: this.selectedNews?.image || ''
    };
  }


  onBackFromPreview(): void {
    this.currentMode = this.selectedNews?.id ? Mode.Edit : Mode.Create;
    this.showForm = true;
  }

  resetForm(): void {
    this.form.reset();
  }

  onGoBack(): void {
    this.showForm = false;
    this.selectedNews = null;
    this.currentMode = null;
    this.toggleTabs.emit(false);
    this.router.navigate(['admin/news']).then(() => {
      if (this.isCreatedMode) {
        this.getNews();
      }
    });
  }

  getNews(): void {
    const params = {
      skipCount: (this.currentPage - 1) * this.selectedPageSize,
      takeCount: this.selectedPageSize,
      orderField: 'Id',
      isOrderByDescending: true,
    };

    this.newsService.getAllNews(params).subscribe((newsArticles) => {
      this.news = newsArticles.map((article) => ({
        ...article,
        text: article.shortBody || article.body,
        author: article.autorName,
        dateTime: article.publishDate ? new Date(article.publishDate) : new Date(),
        views: article.viewsCount,
      }));
    });

    if (this.totalNewsCount === 0) {
      this.newsService.getNewsCount().subscribe((count: number) => {
        this.totalNewsCount = count;
      });
    }
  }

  onPageSelect(page: number): void {
    this.currentPage = page;
    this.getNews();
  }

  onItemsPerPageChange(newPageSize: number): void {
    this.selectedPageSize = newPageSize;
    this.currentPage = 1;
    this.getNews();
  }

  onViewNews(newsItem: NewsArticle): void {
    this.router.navigate(['/admin/news', newsItem.id]).then(() => {
      this.currentMode = Mode.View;
      this.showForm = true;
      this.toggleTabs.emit(true);
    });
  }

  onViewRelatedNews(newsId: number): void {
    const selectedNews = this.news.find(item => item.id === newsId);
    if (selectedNews) {
      this.selectedNews = selectedNews;
      this.currentMode = Mode.View;
      this.showForm = false;
    }
  }

  onDeleteNews(newsItem: NewsArticle): void {   
    this.selectedNews = newsItem;
    this.onConfirmDelete();
  }

  onConfirmDelete(): void {
    if (!this.selectedNews) return;

    const title = this.translate.instant('messages.confirm_deletion');
    const message = this.translate.instant('messages.delete_confirmation_message');
    const confirmLabel = this.translate.instant('buttons.delete');
    const cancelLabel = this.translate.instant('buttons.cancel');

    this.openConfirmationModal(
      title,
      message,
      () => {
        this.newsService.deleteNews(this.selectedNews!.id).subscribe(() => {
          this.toastr.show(this.translate.instant('messages.news_successfully_deleted'), ToastTypesEnum.SUCCESS);
          this.currentMode = null;
          this.selectedNews = null;
          this.router.navigate(['admin/news'])
          this.getNews();
        });
      },
      confirmLabel,
      cancelLabel
    );
  }

  openConfirmationModal(
    title: string,
    message: string,
    confirmAction: () => void,
    confirmLabel:'buttons.confirm',
    cancelLabel: 'buttons.cancel'
  ): void {
    const modalRef = this.modalService.open(ConfirmationDialogComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'confirmation-modal'
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.notConfirmActionLabel = cancelLabel;
    modalRef.componentInstance.confirmActionLabel = confirmLabel;

    modalRef.result.then(
      (result) => {
        if (result) {
          confirmAction();
        }
      },
    );
  }

  addRelatedNewsRow() {
    this.addRelatedNews = Math.floor(Math.random() * 1000) + 1;
    this.relatedNewsEdit = true;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
